import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Image as ImageIcon,
    Maximize2,
    X,
    ZoomIn,
    ZoomOut,
} from 'lucide-react';

import { publicApi } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fadeUp, stagger } from '@/lib/motion';
import { trackEvent } from '@/lib/analytics';
import { usePublicContent } from '@/lib/usePublicContent';

const CATEGORIES = [
    { key: 'all', label: 'Todas' },
    { key: 'hero', label: 'Destacados' },
    { key: 'attractions', label: 'Atracciones' },
    { key: 'arcade', label: 'Arcade' },
    { key: 'events', label: 'Eventos' },
    { key: 'social', label: 'Social' },
    { key: 'gallery', label: 'Galería' },
];

const getCollapsedLimit = () => {
    if (typeof window === 'undefined') return 8;

    if (window.innerWidth >= 1024) return 8;
    if (window.innerWidth >= 768) return 6;

    return 4;
};

export const Gallery = () => {
    const { content = {} } = usePublicContent();

    const [adminMedia, setAdminMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cat, setCat] = useState('all');
    const [expanded, setExpanded] = useState(false);
    const [collapsedLimit, setCollapsedLimit] = useState(
        getCollapsedLimit
    );

    const [selectedIndex, setSelectedIndex] = useState(null);
    const [zoom, setZoom] = useState(1);

    useEffect(() => {
        publicApi
            .getMedia()
            .then((data) => {
                setAdminMedia(Array.isArray(data) ? data : []);
            })
            .catch(() => setAdminMedia([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const onResize = () => {
            setCollapsedLimit(getCollapsedLimit());
        };

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    const allMedia = useMemo(() => {
        const seen = new Set();

        return adminMedia
            .filter((item) => item?.url)
            .sort(
                (a, b) =>
                    Number(a.order || 0) -
                    Number(b.order || 0)
            )
            .filter((item) => {
                if (seen.has(item.url)) return false;

                seen.add(item.url);
                return true;
            });
    }, [adminMedia]);

    const visible = useMemo(() => {
        if (cat === 'all') return allMedia;

        return allMedia.filter(
            (item) =>
                (item.category || 'gallery') === cat
        );
    }, [allMedia, cat]);

    const displayedMedia = useMemo(() => {
        if (expanded) return visible;

        return visible.slice(0, collapsedLimit);
    }, [visible, expanded, collapsedLimit]);

    const hasMore = visible.length > collapsedLimit;

    const selectedItem =
        selectedIndex !== null
            ? visible[selectedIndex]
            : null;

    const title =
        content.gallery_title ||
        'Galería';

    const sub =
        content.gallery_subtitle ||
        "Momentos K'che que pronto vivirás con nosotros";

    const closeLightbox = useCallback(() => {
        setSelectedIndex(null);
        setZoom(1);
    }, []);

    const showPrevious = useCallback(() => {
        if (!visible.length) return;

        setSelectedIndex((current) => {
            if (current === null) return 0;

            return (
                (current - 1 + visible.length) %
                visible.length
            );
        });

        setZoom(1);
    }, [visible.length]);

    const showNext = useCallback(() => {
        if (!visible.length) return;

        setSelectedIndex((current) => {
            if (current === null) return 0;

            return (current + 1) % visible.length;
        });

        setZoom(1);
    }, [visible.length]);

    const zoomIn = useCallback(() => {
        setZoom((current) =>
            Math.min(current + 0.5, 3)
        );
    }, []);

    const zoomOut = useCallback(() => {
        setZoom((current) =>
            Math.max(current - 0.5, 1)
        );
    }, []);

    const openLightbox = (index, item) => {
        setSelectedIndex(index);
        setZoom(1);

        trackEvent(
            'gallery_click',
            window.location.pathname,
            {
                id: item.id,
                category: item.category,
            }
        );
    };

    const changeCategory = (value) => {
        setCat(value);
        setExpanded(false);
        closeLightbox();
    };

    useEffect(() => {
        if (selectedIndex === null) return undefined;

        const previousOverflow =
            document.body.style.overflow;

        document.body.style.overflow = 'hidden';

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeLightbox();
            }

            if (event.key === 'ArrowLeft') {
                showPrevious();
            }

            if (event.key === 'ArrowRight') {
                showNext();
            }

            if (
                event.key === '+' ||
                event.key === '='
            ) {
                zoomIn();
            }

            if (event.key === '-') {
                zoomOut();
            }
        };

        window.addEventListener(
            'keydown',
            handleKeyDown
        );

        return () => {
            document.body.style.overflow =
                previousOverflow;

            window.removeEventListener(
                'keydown',
                handleKeyDown
            );
        };
    }, [
        selectedIndex,
        closeLightbox,
        showPrevious,
        showNext,
        zoomIn,
        zoomOut,
    ]);

    return (
        <>
            <section
                data-testid="home-gallery"
                className="relative bg-kche-soft py-12 sm:py-14 lg:py-16"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mb-6">
                        <p className="text-sm font-bold font-display text-[hsl(var(--kche-pink))] uppercase tracking-wider">
                            Galería
                        </p>

                        <h2 className="mt-1 text-3xl sm:text-4xl lg:text-5xl font-display font-bold">
                            {title}
                        </h2>

                        <p className="mt-2 text-muted-foreground max-w-prose">
                            {sub}
                        </p>
                    </div>

                    <Tabs
                        value={cat}
                        onValueChange={changeCategory}
                    >
                        <TabsList
                            data-testid="gallery-category-tabs"
                            className="flex flex-wrap gap-1 h-auto p-1.5 bg-white rounded-2xl shadow-soft"
                        >
                            {CATEGORIES.map((category) => (
                                <TabsTrigger
                                    key={category.key}
                                    value={category.key}
                                    data-testid={`gallery-tab-${category.key}`}
                                    className="data-[state=active]:bg-gradient-fun data-[state=active]:text-white data-[state=active]:shadow-pop rounded-xl px-4 py-2 text-sm font-bold font-display transition"
                                >
                                    {category.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>

                    <div
                        data-testid="gallery-grid"
                        className="mt-6 min-h-[220px]"
                    >
                        {loading ? (
                            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                                {Array.from({
                                    length: 8,
                                }).map((_, index) => (
                                    <div
                                        key={index}
                                        className="aspect-[4/3] rounded-2xl bg-white border border-border shadow-soft animate-pulse"
                                    />
                                ))}
                            </div>
                        ) : visible.length === 0 ? (
                            <div className="rounded-3xl bg-white border border-dashed border-border p-8 text-center">
                                <div className="mx-auto size-12 rounded-2xl bg-[hsl(var(--kche-cyan))]/15 inline-flex items-center justify-center">
                                    <ImageIcon className="size-6 text-[hsl(var(--kche-cyan))]" />
                                </div>

                                <h3 className="mt-3 font-display font-semibold">
                                    Pronto más contenido aquí
                                </h3>

                                <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
                                    Aún no hay imágenes en esta
                                    categoría. Carga nuevas imágenes
                                    desde el panel administrativo.
                                </p>
                            </div>
                        ) : (
                            <>
                                <motion.div
                                    key={`${cat}-${expanded}`}
                                    initial="hidden"
                                    animate="show"
                                    variants={stagger}
                                    className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
                                >
                                    {displayedMedia.map(
                                        (item, index) => (
                                            <motion.button
                                                key={
                                                    item.id ||
                                                    item.url
                                                }
                                                variants={fadeUp}
                                                data-testid="gallery-item"
                                                type="button"
                                                onClick={() =>
                                                    openLightbox(
                                                        index,
                                                        item
                                                    )
                                                }
                                                className="group relative aspect-[4/3] overflow-hidden rounded-2xl bg-white border border-border shadow-soft hover:shadow-pop transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--kche-pink))]"
                                                aria-label="Ampliar imagen"
                                            >
                                                <img
                                                    src={
                                                        item.url
                                                    }
                                                    alt={
                                                        item.alt ||
                                                        "K'che"
                                                    }
                                                    loading="lazy"
                                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                />

                                                <span className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                                                <span className="absolute right-3 top-3 inline-flex size-9 items-center justify-center rounded-full bg-white/90 text-[hsl(var(--kche-dark))] opacity-0 scale-90 shadow-soft transition-all group-hover:opacity-100 group-hover:scale-100">
                                                    <Maximize2 className="size-4" />
                                                </span>
                                            </motion.button>
                                        )
                                    )}
                                </motion.div>

                                {hasMore ? (
                                    <div className="mt-6 flex justify-center">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setExpanded(
                                                    (current) =>
                                                        !current
                                                )
                                            }
                                            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-display text-sm font-bold text-[hsl(var(--kche-pink))] border border-border shadow-soft hover:shadow-pop hover:scale-[1.03] transition"
                                            aria-expanded={
                                                expanded
                                            }
                                        >
                                            {expanded
                                                ? 'Ver menos'
                                                : `Ver más (${visible.length - collapsedLimit})`}

                                            {expanded ? (
                                                <ChevronUp className="size-5" />
                                            ) : (
                                                <ChevronDown className="size-5" />
                                            )}
                                        </button>
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                </div>
            </section>

            <AnimatePresence>
                {selectedItem ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-3 sm:p-6"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Galería ampliada"
                        onMouseDown={(event) => {
                            if (
                                event.target ===
                                event.currentTarget
                            ) {
                                closeLightbox();
                            }
                        }}
                    >
                        <button
                            type="button"
                            onClick={closeLightbox}
                            className="absolute right-4 top-4 z-30 inline-flex size-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur hover:bg-white/25 transition"
                            aria-label="Cerrar galería"
                        >
                            <X className="size-6" />
                        </button>

                        <div className="absolute left-1/2 top-4 z-30 -translate-x-1/2 rounded-full bg-black/45 px-4 py-2 text-sm font-bold text-white backdrop-blur">
                            {selectedIndex + 1} /{' '}
                            {visible.length}
                        </div>

                        {visible.length > 1 ? (
                            <>
                                <button
                                    type="button"
                                    onClick={showPrevious}
                                    className="absolute left-3 sm:left-6 z-30 inline-flex size-11 sm:size-12 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur hover:bg-white/25 transition"
                                    aria-label="Imagen anterior"
                                >
                                    <ChevronLeft className="size-7" />
                                </button>

                                <button
                                    type="button"
                                    onClick={showNext}
                                    className="absolute right-3 sm:right-6 z-30 inline-flex size-11 sm:size-12 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur hover:bg-white/25 transition"
                                    aria-label="Imagen siguiente"
                                >
                                    <ChevronRight className="size-7" />
                                </button>
                            </>
                        ) : null}

                        <motion.div
                            initial={{
                                opacity: 0,
                                scale: 0.96,
                            }}
                            animate={{
                                opacity: 1,
                                scale: 1,
                            }}
                            exit={{
                                opacity: 0,
                                scale: 0.96,
                            }}
                            transition={{
                                duration: 0.22,
                            }}
                            className="relative flex h-[82vh] w-full max-w-6xl items-center justify-center overflow-auto rounded-2xl"
                        >
                            <img
                                key={selectedItem.url}
                                src={selectedItem.url}
                                alt={
                                    selectedItem.alt ||
                                    "K'che"
                                }
                                draggable="false"
                                onDoubleClick={() =>
                                    setZoom((current) =>
                                        current === 1
                                            ? 2
                                            : 1
                                    )
                                }
                                className="max-h-full max-w-full object-contain select-none transition-transform duration-300 ease-out"
                                style={{
                                    transform: `scale(${zoom})`,
                                }}
                            />
                        </motion.div>

                        <div className="absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/55 p-2 text-white backdrop-blur">
                            <button
                                type="button"
                                onClick={zoomOut}
                                disabled={zoom <= 1}
                                className="inline-flex size-10 items-center justify-center rounded-full hover:bg-white/15 disabled:opacity-35 transition"
                                aria-label="Alejar imagen"
                            >
                                <ZoomOut className="size-5" />
                            </button>

                            <span className="min-w-14 text-center text-sm font-bold">
                                {Math.round(zoom * 100)}%
                            </span>

                            <button
                                type="button"
                                onClick={zoomIn}
                                disabled={zoom >= 3}
                                className="inline-flex size-10 items-center justify-center rounded-full hover:bg-white/15 disabled:opacity-35 transition"
                                aria-label="Acercar imagen"
                            >
                                <ZoomIn className="size-5" />
                            </button>
                        </div>

                        {selectedItem.alt ? (
                            <p className="absolute bottom-20 left-1/2 z-20 max-w-xl -translate-x-1/2 px-4 text-center text-sm text-white/85">
                                {selectedItem.alt}
                            </p>
                        ) : null}
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </>
    );
};

export default Gallery;